import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { BOLT_GUIDANCE, boltRating } from '../../data/apneaProtocols';
import { formatSeconds } from '../../utils/apneaTables';
import TimeEntry from './TimeEntry';

/**
 * BOLT quick entry.
 *
 * BOLT is the daily signal the program leans on, and it is measured before getting
 * out of bed — so hand entry is the primary path here, not a fallback. The "time it"
 * option exists for anyone who does want the app to run the stopwatch.
 */

interface BoltQuickEntryProps {
  /** Prior readings, for the plausibility check and the streak note. */
  history: number[];
  /** True when a reading has already been logged today. */
  loggedToday?: boolean;
  saving?: boolean;
  error?: string | null;
  onSave: (seconds: number, source: 'manual' | 'corrected') => void;
}

const BoltQuickEntry: React.FC<BoltQuickEntryProps> = ({
  history,
  loggedToday = false,
  saving = false,
  error = null,
  onSave,
}) => {
  const [seconds, setSeconds] = useState<number | null>(null);
  const [source, setSource] = useState<'manual' | 'corrected'>('manual');
  const [timing, setTiming] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [, tick] = useState(0);

  // Count up from a timestamp, consistent with the session runner: never
  // accumulate ticks, so a backgrounded tab cannot lose time.
  React.useEffect(() => {
    if (!timing) return undefined;
    const id = window.setInterval(() => tick(n => n + 1), 250);
    return () => window.clearInterval(id);
  }, [timing]);

  const elapsed = startedAt === null ? 0 : Math.floor((Date.now() - startedAt) / 1000);

  const stopTimer = () => {
    setTiming(false);
    setSeconds(elapsed);
    setSource('manual');
    setStartedAt(null);
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">BOLT / control pause</Typography>
        {loggedToday && <Chip size="small" color="success" label="logged today" />}
      </Stack>

      {timing ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {formatSeconds(elapsed)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Stop at the first definite urge to breathe.
          </Typography>
          <Button variant="contained" size="large" onClick={stopTimer} sx={{ mt: 2, minWidth: 160 }}>
            Stop
          </Button>
        </Box>
      ) : (
        <TimeEntry
          label="BOLT"
          value={seconds}
          history={history}
          onChange={(value, src) => { setSeconds(value); setSource(src); }}
          helperText="Enter m:ss or seconds"
          onStartTimer={() => {
            setStartedAt(Date.now());
            setSeconds(null);
            setTiming(true);
          }}
        />
      )}

      {seconds !== null && !timing && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {formatSeconds(seconds)} — {boltRating(seconds)}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {!timing && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            disabled={seconds === null || saving}
            onClick={() => seconds !== null && onSave(seconds, source)}
          >
            {saving ? 'Saving…' : 'Log BOLT'}
          </Button>
          {seconds !== null && (
            <Button onClick={() => setSeconds(null)} disabled={saving}>Clear</Button>
          )}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {BOLT_GUIDANCE}
      </Typography>
    </Paper>
  );
};

export default BoltQuickEntry;
